import React, { useRef, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { motion } from 'framer-motion';

interface AgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  title: string;
  content: string;
}

const AgreementModal = ({ isOpen, onClose, onAccept, title, content }: AgreementModalProps) => {
  const [canAccept, setCanAccept] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleScroll = () => {
    if (textareaRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = textareaRef.current;
      const isBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 10;
      setCanAccept(isBottom);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setCanAccept(false);
      // Disable body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      // Re-enable body scroll when modal is closed
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/50"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-lg"
      >
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <textarea
          ref={textareaRef}
          onScroll={handleScroll}
          value={content.replace(/<[^>]*>/g, '')} // Remove HTML tags
          readOnly
          className="w-full p-6 h-[60vh] resize-none bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-0 focus:ring-0 rounded-none font-mono text-sm leading-relaxed"
        />

        <div className="flex items-center justify-between gap-4 p-4 border-t dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {canAccept ? 
              '✓ Sözleşme sonuna ulaştınız' : 
              'Devam etmek için sözleşmeyi sonuna kadar okuyunuz'}
          </div>
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Kapat
            </button>
            <button
              onClick={onAccept}
              disabled={!canAccept}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 ${
                canAccept
                  ? 'bg-blue-600 hover:bg-blue-700 transform hover:scale-105'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              Okudum ve Onaylıyorum
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AgreementModal; 